import type { RegistrationKind } from './domain-messages';
import type { Locale } from './messages';

/**
 * Pays de l'entreprise et identifiant d'entreprise propre à chaque pays. Le SIREN n'existe qu'en
 * France : ailleurs, on demande l'identifiant du registre national quand on sait le contrôler, et
 * sinon un numéro libre, sans contrôle inventé. Les noms de pays viennent d'`Intl.DisplayNames`,
 * dans la langue de la page. Codes ISO 3166-1 alpha-2.
 */

export const COUNTRIES = [
  'FR', 'BE', 'CH', 'LU', 'MC', 'DE', 'ES', 'IT', 'PT', 'NL', 'AT', 'IE', 'GB', 'DK', 'SE', 'NO', 'FI', 'PL', 'CZ', 'GR',
  'CA', 'US', 'MA', 'TN', 'DZ', 'SN', 'CI', 'CM',
] as const;

export type CountryCode = (typeof COUNTRIES)[number];

export function isCountry(value: string): value is CountryCode {
  return (COUNTRIES as readonly string[]).includes(value);
}

export function countryName(code: string, locale: Locale): string {
  try {
    return new Intl.DisplayNames([locale], { type: 'region' }).of(code) ?? code;
  } catch {
    return code;
  }
}

/** Pays proposé par défaut : celui de la langue du navigateur s'il est connu, sinon la France. */
export function defaultCountry(languages: readonly string[]): CountryCode {
  for (const tag of languages) {
    const region = tag.split('-')[1]?.toUpperCase();
    if (region !== undefined && isCountry(region)) return region;
  }
  return 'FR';
}

export type RegistrationIssue = 'format' | 'checksum';

export interface RegistrationRule {
  /** Schéma enregistré avec la valeur : `FR_SIREN`, `FR_SIRET`, `BE_BCE`, `CH_UID`, `LU_RCS`, `OTHER`. */
  scheme(value: string): string;
  /** Libellé et aide : domain-messages, par langue. */
  kind: RegistrationKind;
  placeholder?: string;
  inputMode: 'numeric' | 'text';
  normalize(value: string): string;
  check(value: string): RegistrationIssue | null;
}

function luhn(digits: string): boolean {
  let sum = 0;
  for (let i = 0; i < digits.length; i += 1) {
    let digit = Number(digits[digits.length - 1 - i]);
    if (i % 2 === 1) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }
  return sum % 10 === 0;
}

const digitsOnly = (value: string): string => value.replace(/[\s.\-/]/g, '');

const FRANCE: RegistrationRule = {
  kind: 'FR',
  scheme: (value) => (digitsOnly(value).length === 14 ? 'FR_SIRET' : 'FR_SIREN'),
  placeholder: '123 456 789',
  inputMode: 'numeric',
  normalize: digitsOnly,
  check(value) {
    const digits = digitsOnly(value);
    if (!/^\d+$/.test(digits) || (digits.length !== 9 && digits.length !== 14)) return 'format';
    // La Poste (SIREN 356000000) déroge à la clé de Luhn pour ses SIRET.
    if (digits.length === 14 && digits.startsWith('356000000')) return null;
    return luhn(digits) ? null : 'checksum';
  },
};

const BELGIUM: RegistrationRule = {
  kind: 'BE',
  scheme: () => 'BE_BCE',
  placeholder: '0403.170.701',
  inputMode: 'numeric',
  normalize: (value) => digitsOnly(value).replace(/^BE/i, ''),
  check(value) {
    const digits = digitsOnly(value).replace(/^BE/i, '');
    if (!/^[01]\d{9}$/.test(digits)) return 'format';
    return 97 - (Number(digits.slice(0, 8)) % 97) === Number(digits.slice(8)) ? null : 'checksum';
  },
};

const SWITZERLAND: RegistrationRule = {
  kind: 'CH',
  scheme: () => 'CH_UID',
  placeholder: 'CHE-123.456.789',
  inputMode: 'text',
  normalize: (value) => `CHE${digitsOnly(value).replace(/^CHE/i, '')}`,
  check(value) {
    const digits = digitsOnly(value).replace(/^CHE/i, '');
    if (!/^\d{9}$/.test(digits)) return 'format';
    const weights = [5, 4, 3, 2, 7, 6, 5, 4];
    const sum = weights.reduce((total, weight, i) => total + weight * Number(digits[i]), 0);
    const key = 11 - (sum % 11);
    if (key === 10) return 'checksum';
    return (key === 11 ? 0 : key) === Number(digits[8]) ? null : 'checksum';
  },
};

const LUXEMBOURG: RegistrationRule = {
  kind: 'LU',
  scheme: () => 'LU_RCS',
  placeholder: 'B123456',
  inputMode: 'text',
  normalize: (value) => value.replace(/\s/g, '').toUpperCase(),
  check: (value) => (/^[A-Z]\d{1,7}$/.test(value.replace(/\s/g, '').toUpperCase()) ? null : 'format'),
};

/** Ailleurs : numéro libre, sans contrôle, gardé tel quel avec le pays. */
const OTHER: RegistrationRule = {
  kind: 'OTHER',
  scheme: () => 'OTHER',
  inputMode: 'text',
  normalize: (value) => value.trim(),
  check: (value) => (value.trim().length >= 3 && value.trim().length <= 40 ? null : 'format'),
};

export function registrationRule(country: string): RegistrationRule {
  if (country === 'FR') return FRANCE;
  if (country === 'BE') return BELGIUM;
  if (country === 'CH') return SWITZERLAND;
  if (country === 'LU') return LUXEMBOURG;
  return OTHER;
}

/** Identifiant facultatif : vide → aucun problème. */
export function registrationIssue(country: string, value: string): RegistrationIssue | null {
  if (value.trim() === '') return null;
  return registrationRule(country).check(value);
}

export interface CompanyRegistration {
  country: string;
  scheme: string;
  value: string;
}

export function companyRegistration(country: string, value: string): CompanyRegistration | undefined {
  if (value.trim() === '') return undefined;
  const rule = registrationRule(country);
  return { country, scheme: rule.scheme(value), value: rule.normalize(value) };
}
